import React, { FunctionComponent, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { isNonEmptyString } from 'ts-utils';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { LoadingBar, LoadingBarProps } from '../../styles/LoadingBar';
import { BodyXXS } from '../../styles/Typography';

// Delay before navigating to next screen after all bars complete
const NAVIGATION_DELAY_MS = 2000;

export type AiLoadingLayoutConfig = {
  title: string;
  subtitle?: string;
  loadingBars: LoadingBarProps[];
  footnoteHeader?: string;
  footnote?: string;
  /** Show the "AI enhanced" badge. Defaults to true; set false for non-AI contexts. */
  showAiBadge?: boolean;
  /** Optional social-proof card rendered below the loading box. */
  socialProof?: { count: string | number; caption: string };
  onComplete?: () => void;
};

export const AiLoadingLayout: FunctionComponent<AiLoadingLayoutConfig> = (props) => {
  const { title, subtitle, loadingBars, footnoteHeader, footnote, socialProof, onComplete } = props;

  const [completedBars, setCompletedBars] = useState<number[]>([]);
  const [startedBars, setStartedBars] = useState<number[]>([0]); // Start with first bar

  const allBarsComplete = completedBars.length === loadingBars.length;

  const handleBarComplete = (index: number) => {
    setCompletedBars((prev) => {
      if (prev.includes(index)) return prev; // Avoid duplicates
      return [...prev, index];
    });

    // Start the next bar when current one completes
    if (index + 1 < loadingBars.length) {
      setStartedBars((prevStarted) => {
        if (prevStarted.includes(index + 1)) return prevStarted; // Avoid duplicates
        return [...prevStarted, index + 1];
      });
    }
  };

  // Handle static bars (where startNumber === endNumber) by completing them immediately when they start
  useEffect(() => {
    startedBars.forEach((barIndex) => {
      const loadingBarProps = loadingBars[barIndex];
      const isStaticBar = loadingBarProps.startNumber === loadingBarProps.endNumber;

      if (isStaticBar && !completedBars.includes(barIndex)) {
        // Complete static bars immediately after a small delay
        setTimeout(() => handleBarComplete(barIndex), 100);
      }
    });
  }, [startedBars, loadingBars, completedBars]);

  useEffect(
    function goToNextScreenOnLoadingComplete() {
      if (allBarsComplete && onComplete !== undefined) {
        setTimeout(() => onComplete(), NAVIGATION_DELAY_MS);
      }
    },
    [allBarsComplete, onComplete]
  );

  return (
    <>
      <LoadingContainer data-test="aiLoadingContainer">
        <TitleSection>
          <TitleRow>
            <Title>{title}</Title>
          </TitleRow>
          {isNonEmptyString(subtitle) && (
            <SubtitleRow>
              <SubtitleText>{subtitle}</SubtitleText>
            </SubtitleRow>
          )}
        </TitleSection>

        <LoadingBarsContainer>
          {loadingBars.map((loadingBarProps, index) => {
            const shouldStart = startedBars.includes(index);
            const isStaticBar = loadingBarProps.startNumber === loadingBarProps.endNumber;

            return (
              <LoadingBar
                key={`${loadingBarProps.title}-${index}-${shouldStart ? 'started' : 'waiting'}`}
                {...loadingBarProps}
                delayMs={shouldStart ? (loadingBarProps.delayMs ?? 0) : 10000}
                startNumber={loadingBarProps.startNumber}
                endNumber={shouldStart ? loadingBarProps.endNumber : loadingBarProps.startNumber}
                durationMs={shouldStart ? loadingBarProps.durationMs : 0}
                isStatic={isStaticBar}
                onComplete={
                  shouldStart && !isStaticBar ? () => handleBarComplete(index) : undefined
                }
              />
            );
          })}
        </LoadingBarsContainer>

        <TextSection>
          <OptimizingTextContainer>
            <FootnoteHeaderText>{footnoteHeader}</FootnoteHeaderText>
            <AnimatedDots isComplete={allBarsComplete}>
              <span>●</span>
              <span>●</span>
              <span>●</span>
            </AnimatedDots>
          </OptimizingTextContainer>
          <BodyXXS
            style={{
              textAlign: 'left',
              visibility: allBarsComplete && isNonEmptyString(footnote) ? 'visible' : 'hidden',
            }}
          >
            {isNonEmptyString(footnote) ? footnote : ' '}
          </BodyXXS>
        </TextSection>
      </LoadingContainer>
      {socialProof && (
        <SocialProofCard>
          <SocialProofCount>{socialProof.count}</SocialProofCount>
          <SocialProofCaption>{socialProof.caption}</SocialProofCaption>
        </SocialProofCard>
      )}
    </>
  );
};

const SocialProofCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  margin-top: 16px;
  border: 1px solid ${Color.decorative.muted};
  border-radius: 12px;
  padding: 16px 20px;
`;

const SocialProofCount = styled.span`
  font-family: ${({ theme }) => theme.font.heading};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: 28px;
  line-height: 1;
  color: ${Color.content.default};
`;

const SocialProofCaption = styled(BodyXXS)`
  color: ${Color.content.muted};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid
    ${appOverride({
      nerva: Color.decorative.muted,
      default: Color.decorative.muted,
    })};
  border-radius: 12px;
  padding: 20px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const SubtitleText = styled(BodyXXS)`
  color: ${Color.content.muted};
`;

const FootnoteHeaderText = styled(BodyXXS)`
  color: ${Color.content.muted};
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.font.heading};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1;
  color: ${Color.content.default};
  white-space: nowrap;
`;

const LoadingBarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptimizingTextContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
`;

const dotsScaleAnimation = keyframes`
  0%, 66%, 100% { 
    transform: scale(0.8);
  }
  33% { 
    transform: scale(1.2);
  }
`;

const AnimatedDots = styled.span<{ isComplete: boolean }>`
  color: ${appOverride({ nerva: Color.content.default, default: Color.decorative.muted })};
  display: inline-block;
  gap: 2px;

  & > span {
    display: inline-block;
    font-size: 8px;
    animation: ${({ isComplete }) => (isComplete ? 'none' : dotsScaleAnimation)} 1.5s infinite;
    margin: 0 1px;
    transform: ${({ isComplete }) => (isComplete ? 'scale(0.8)' : 'initial')};
  }

  & > span:nth-child(1) {
    animation-delay: 0s;
  }
  & > span:nth-child(2) {
    animation-delay: 0.5s;
  }
  & > span:nth-child(3) {
    animation-delay: 1s;
  }
`;
